import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import ManageEventStepper from './components/ManageEventStepper';
import ManageEventActions from './components/ManageEventActions';
import { isEventDatePast } from './eventTimeUtils';
import BasicInfoStep from './steps/BasicInfoStep';
import DateLocationStep from './steps/DateLocationStep';
import MenuStep from './steps/MenuStep';
import SettingsPoliciesStep from './steps/SettingsPoliciesStep';
import ConfirmationStep from './steps/ConfirmationStep';
import EventSubmissionSuccessModal from '../../Partner/Events/widgets/EventSubmissionSuccessModal';
import { buildSubmittedEventSummary } from '../../Partner/Events/submittedEventSummary';
import {
  getInitialFormState,
  mapEventToFormState,
  mapEventToDuplicateFormState,
  getManageEventSteps,
  shouldShowEventSubmissionSuccessModal,
} from './manageEventForm';
import {
  buildAddEventFormData,
  buildEditEventFormData,
  buildPartnerEventFormData,
} from './buildEventFormData';
import {
  validateManageEventStep,
  validateAllStepsBeforeConfirmation,
  validateBasicInfoStep,
  getCoverImageSubmitBlockMessage,
} from './validateManageEventStep';
import {
  canEditPartnerEvent,
  getEventWorkflowStatus,
  useEventsScope,
} from '../../../contexts/EventsScopeContext';
import { useEventQuery } from '../../../api/use-event-query';
import { useAddEventMutation } from '../../../api/use-add-event-mutation';
import { useEditEventMutation } from '../../../api/use-edit-event-mutation';
import { useCoverImageOptimization } from './useCoverImageOptimization';
import { BRAND_ORANGE } from './components/manageEventFieldStyles';
import { useAuth } from '../../../contexts/AuthContext';
import EventLivePreview from './components/EventLivePreview';
import { PortalPageHeader } from '../../layout/PortalPageHeader';

const FormPanel = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 16,
  padding: theme.spacing(3.5),
  boxShadow: '0 1px 3px rgba(0,0,0,.04)',
}));

const clearErrorsForField = (errors, field) => {
  const next = { ...errors };
  delete next[field];

  if (field === 'menuSections') {
    Object.keys(next).forEach((key) => {
      if (key === 'menu' || key.startsWith('dish_')) delete next[key];
    });
  }

  if (field === 'coverImageFile' || field === 'coverImagePreview') {
    delete next.coverImage;
  }

  if (['location', 'latitude', 'longitude'].includes(field)) {
    delete next.location;
    delete next.latitude;
    delete next.longitude;
  }

  if (field === 'venueName') {
    delete next.venueName;
  }

  if (field === 'onlineMeetingLink') {
    delete next.onlineMeetingLink;
  }

  if (field === 'eventFormat') {
    delete next.eventFormat;
    delete next.venueName;
    delete next.location;
    delete next.latitude;
    delete next.longitude;
    delete next.onlineMeetingLink;
  }

  return next;
};

const ManageEvent = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { routes, queryKeys, features } = useEventsScope();
  const { partnerProfile, isPartner } = useAuth();
  const isEditMode = Boolean(eventId);
  const duplicateFromId = searchParams.get('duplicateFrom');
  const initialDuplicateFormRef = useRef(undefined);
  if (initialDuplicateFormRef.current === undefined) {
    initialDuplicateFormRef.current = location.state?.duplicateForm ?? null;
  }
  const initialDuplicateForm = initialDuplicateFormRef.current;
  const isDuplicating = !isEditMode && (Boolean(initialDuplicateForm) || Boolean(duplicateFromId));
  const { hasSettingsStep: defaultHasSettingsStep } = features;

  const {
    data: eventData,
    isLoading: isEventLoading,
    isError: isEventError,
    error: eventError,
  } = useEventQuery(eventId, { enabled: isEditMode });

  const {
    data: duplicateEventData,
    isLoading: isDuplicateLoading,
    isError: isDuplicateError,
    error: duplicateError,
  } = useEventQuery(duplicateFromId, {
    enabled: !isEditMode && Boolean(duplicateFromId) && !initialDuplicateForm,
  });

  const addEventMutation = useAddEventMutation();
  const editEventMutation = useEditEventMutation();

  const includeSettingsStep = defaultHasSettingsStep;

  const manageEventSteps = useMemo(
    () => getManageEventSteps(includeSettingsStep),
    [includeSettingsStep],
  );

  const stepComponents = useMemo(() => {
    const components = [BasicInfoStep, DateLocationStep, MenuStep];
    if (includeSettingsStep) {
      components.push(SettingsPoliciesStep);
    }
    components.push(ConfirmationStep);
    return components;
  }, [includeSettingsStep]);

  const maxStepIndex = manageEventSteps.length - 1;
  const activeStep = useMemo(() => {
    const raw = Number(searchParams.get('step'));
    if (!Number.isFinite(raw) || raw < 0) return 0;
    return Math.min(maxStepIndex, Math.floor(raw));
  }, [searchParams, maxStepIndex]);

  const goToStep = useCallback(
    (step, { replace = false } = {}) => {
      const clamped = Math.max(0, Math.min(maxStepIndex, step));
      setStepErrors({});
      const nextParams = new URLSearchParams(searchParams);
      if (clamped <= 0) {
        nextParams.delete('step');
      } else {
        nextParams.set('step', String(clamped));
      }
      const search = nextParams.toString();
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace });
    },
    [location.pathname, maxStepIndex, navigate, searchParams],
  );

  const [form, setForm] = useState(() => initialDuplicateForm ?? getInitialFormState());
  const [formHydrated, setFormHydrated] = useState(() => {
    if (isEditMode) return false;
    return Boolean(initialDuplicateForm) || !duplicateFromId;
  });
  const [stepErrors, setStepErrors] = useState({});
  const [submittingAction, setSubmittingAction] = useState(null);
  const [submittedEventSummary, setSubmittedEventSummary] = useState(null);
  const hydratedEventIdRef = useRef(null);
  const hydratedDuplicateIdRef = useRef(null);

  const workflowStatus = isEditMode ? getEventWorkflowStatus(eventData) : null;
  const isPastEventEditLocked =
    isEditMode && workflowStatus !== 'draft' && isEventDatePast(eventData?.eventDate);
  const isShowingSubmissionSuccess = Boolean(submittedEventSummary);
  const isPartnerEditLocked =
    !isShowingSubmissionSuccess &&
    isEditMode &&
    features.requiresSaveAs &&
    !canEditPartnerEvent(eventData, features);

  useEffect(() => {
    if (initialDuplicateForm) {
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: null },
      );
    }
  }, [initialDuplicateForm, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isEditMode) {
      hydratedEventIdRef.current = null;
      return;
    }

    if (!eventData) {
      return;
    }

    if (hydratedEventIdRef.current !== eventId) {
      setForm(mapEventToFormState(eventData));
      setFormHydrated(true);
      hydratedEventIdRef.current = eventId;
    }
  }, [isEditMode, eventData, eventId]);

  useEffect(() => {
    if (isEditMode || initialDuplicateForm || !duplicateFromId || !duplicateEventData) {
      return;
    }

    if (hydratedDuplicateIdRef.current === duplicateFromId) {
      return;
    }

    setForm(mapEventToDuplicateFormState(duplicateEventData));
    setFormHydrated(true);
    hydratedDuplicateIdRef.current = duplicateFromId;
    toast.success('Event details copied. Choose a new event date and submit.');
  }, [isEditMode, initialDuplicateForm, duplicateFromId, duplicateEventData]);

  useEffect(() => {
    if (!isDuplicateError) return;

    toast.error(duplicateError?.message || 'Could not duplicate event');
    setForm(getInitialFormState());
    setFormHydrated(true);
  }, [isDuplicateError, duplicateError]);

  const handleCoverImageChange = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setStepErrors((prev) => clearErrorsForField(prev, 'coverImageFile'));
  }, []);

  const {
    coverImageOptimizing,
    coverImageOptimizationError,
    coverImagePendingUpload,
    handleCoverImageSelected,
    cancelCoverImageOptimization,
  } = useCoverImageOptimization({ form, onCoverImageChange: handleCoverImageChange });

  const coverImageSubmitBlockMessage = getCoverImageSubmitBlockMessage(form, {
    coverImageOptimizing,
    coverImagePendingUpload,
    coverImageOptimizationError,
  });

  if (!isEditMode && (isDuplicateLoading || !formHydrated)) {
    return (
      <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (isEditMode && isEventLoading) {
    return (
      <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (isEditMode && (isEventError || (!isEventLoading && !eventData))) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          {eventError?.message || 'Event not found'}
        </Typography>
        <Button
          onClick={() => navigate(routes.list)}
          sx={{ color: BRAND_ORANGE, textTransform: 'none' }}
        >
          Back to Events
        </Button>
      </Box>
    );
  }

  if (isPastEventEditLocked) {
    return (
      <Box sx={{ py: 6, textAlign: 'center', maxWidth: 480, mx: 'auto' }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          Past events cannot be edited.
        </Typography>
        <Button
          onClick={() => navigate(routes.detail(eventId))}
          sx={{ color: BRAND_ORANGE, textTransform: 'none', mr: 1 }}
        >
          View event
        </Button>
        <Button
          onClick={() => navigate(routes.list)}
          sx={{ color: 'text.secondary', textTransform: 'none' }}
        >
          Back to Events
        </Button>
      </Box>
    );
  }

  if (isPartnerEditLocked) {
    return (
      <>
        <Box sx={{ py: 6, textAlign: 'center', maxWidth: 480, mx: 'auto' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            This event cannot be edited while it is under moderation review.
          </Typography>
          <Button
            onClick={() => navigate(routes.detail(eventId))}
            sx={{ color: BRAND_ORANGE, textTransform: 'none', mr: 1 }}
          >
            View event
          </Button>
          <Button
            onClick={() => navigate(routes.list)}
            sx={{ color: BRAND_ORANGE, textTransform: 'none' }}
          >
            Back to Events
          </Button>
        </Box>
        <EventSubmissionSuccessModal
          open={isShowingSubmissionSuccess}
          eventSummary={submittedEventSummary}
          onGoToEvents={() => {
            setSubmittedEventSummary(null);
            navigate(routes.list);
          }}
        />
      </>
    );
  }

  const pageTitle = isEditMode ? 'Edit Event' : isDuplicating ? 'Duplicate Event' : 'Add New Event';
  const StepComponent = stepComponents[activeStep];
  const isLastStep = activeStep === manageEventSteps.length - 1;
  const validationEntries = Object.entries(stepErrors);
  const validationOptions = { includeSettingsStep };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setStepErrors((prev) => clearErrorsForField(prev, field));
  };

  const handleApplyGeneratedFields = (fields = {}) => {
    setForm((prev) => ({ ...prev, ...fields }));
    setStepErrors((prev) =>
      Object.keys(fields).reduce((next, field) => clearErrorsForField(next, field), prev),
    );
  };

  const handleLocationPlaceSelected = (fields) => {
    setForm((prev) => ({ ...prev, ...fields }));
    setStepErrors((prev) => {
      let next = clearErrorsForField(prev, 'location');
      next = clearErrorsForField(next, 'latitude');
      next = clearErrorsForField(next, 'longitude');
      return next;
    });
  };

  const runStepValidation = (stepIndex) => {
    const result = validateManageEventStep(stepIndex, form, validationOptions);
    if (!result.valid) {
      setStepErrors(result.errors);
    }
    return result;
  };

  const handleCancel = () => {
    if (isEditMode) {
      navigate(routes.detail(eventId));
      return;
    }
    navigate(routes.list);
  };

  const handleBack = () => {
    if (activeStep > 0) {
      navigate(-1);
    }
  };

  const handleHeaderBack = () => {
    if (activeStep > 0) {
      handleBack();
      return;
    }
    handleCancel();
  };

  const handleNext = () => {
    const result = runStepValidation(activeStep);
    if (!result.valid) {
      toast.error('Please complete all required fields before continuing.');
      return;
    }

    goToStep(activeStep + 1);
  };

  const handleSkipMenu = () => {
    goToStep(activeStep + 1);
  };

  const finishPartnerSubmit = (result, action) => {
    if (result?.data?.contentModerationPassed === false) {
      return { handled: true, showModal: false };
    }

    if (
      shouldShowEventSubmissionSuccessModal({
        requiresSaveAs: features.requiresSaveAs,
        isEditMode,
        workflowStatus,
        action,
        form,
        eventData,
      })
    ) {
      setSubmittedEventSummary(buildSubmittedEventSummary(result.data, form));
      void queryClient.invalidateQueries({ queryKey: queryKeys.all });
      return { handled: true, showModal: true };
    }

    return { handled: false, showModal: false };
  };

  const handleSubmit = async (action = 'submit') => {
    const isQuickDraft = action === 'draft';
    const isDraftSaveChanges = action === 'save-changes';
    const isDraft = isQuickDraft || isDraftSaveChanges;

    if (coverImageSubmitBlockMessage) {
      toast.error(coverImageSubmitBlockMessage);
      return;
    }

    const apiSaveAs = isDraft ? 'draft' : 'submit';

    const validation = isQuickDraft
      ? validateBasicInfoStep(form)
      : validateAllStepsBeforeConfirmation(form, validationOptions);

    if (!validation.valid) {
      setStepErrors(validation.errors);
      if (!isQuickDraft && validation.failedStep != null) {
        goToStep(validation.failedStep, { replace: true });
      }
      toast.error(
        isQuickDraft
          ? 'Add an event title before saving as draft.'
          : isDraftSaveChanges
            ? 'Please fix the highlighted fields before saving.'
            : features.requiresSaveAs
              ? 'Please fix the highlighted fields before submitting.'
              : 'Please fix the highlighted fields before publishing.',
      );
      return;
    }

    setStepErrors({});
    setSubmittingAction(action);

    try {
      if (isEditMode) {
        const formData = features.requiresSaveAs
          ? buildPartnerEventFormData(form, { saveAs: apiSaveAs })
          : buildEditEventFormData(form, { includePartnerSettings: includeSettingsStep });
        const result = await editEventMutation.mutateAsync({ eventId, formData });

        toast.success(
          result.message ||
            (isDraftSaveChanges
              ? 'Changes saved successfully'
              : isQuickDraft
                ? features.draftSuccessMessage
                : 'Event updated successfully'),
        );
        const editFinish = finishPartnerSubmit(result, action);
        if (editFinish.handled) {
          return;
        }
        await queryClient.invalidateQueries({ queryKey: queryKeys.all });
        navigate(routes.detail(eventId));
        return;
      }

      let formData;
      if (features.requiresSaveAs) {
        formData = buildPartnerEventFormData(form, { saveAs: apiSaveAs });
      } else {
        formData = buildAddEventFormData(form, { published: true });
      }

      const result = await addEventMutation.mutateAsync(formData);

      toast.success(
        result.message ||
          (isQuickDraft ? features.draftSuccessMessage : features.createSuccessMessage),
      );
      const createFinish = finishPartnerSubmit(result, action);
      if (createFinish.handled) {
        return;
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.all });
      navigate(routes.list);
    } catch (error) {
      toast.error(
        error?.message ||
          (isEditMode
            ? 'Failed to update event. Please try again.'
            : isQuickDraft
              ? features.draftErrorMessage
              : features.createErrorMessage),
      );
    } finally {
      setSubmittingAction(null);
    }
  };

  if (isEditMode && !formHydrated) {
    return (
      <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PortalPageHeader title={pageTitle} onBack={handleHeaderBack} showBack />

      {isEditMode && workflowStatus && features.requiresSaveAs && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Current status: <strong>{workflowStatus.toUpperCase()}</strong>
          {workflowStatus === 'rejected'
            ? ' — you can edit and resubmit this event.'
            : workflowStatus === 'draft'
              ? ' — save changes as draft or submit when you are ready for review.'
              : null}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { xs: 'stretch', lg: 'flex-start' },
          gap: 2.5,
          flex: 1,
          minHeight: 0,
        }}
      >
        <FormPanel sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <ManageEventStepper activeStep={activeStep} steps={manageEventSteps} />
          {validationEntries.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {validationEntries.map(([key, message]) => (
                  <li key={key}>{message}</li>
                ))}
              </Box>
            </Alert>
          )}
          <StepComponent
            form={form}
            errors={stepErrors}
            onChange={handleChange}
            onLocationPlaceSelected={handleLocationPlaceSelected}
            onSkipMenu={handleSkipMenu}
            onApplyGeneratedFields={handleApplyGeneratedFields}
            showDescribePrompt={!isEditMode && !isDuplicating}
            onCoverImageSelected={handleCoverImageSelected}
            coverImageOptimizing={coverImageOptimizing}
            coverImageOptimizationError={coverImageOptimizationError}
            onCancelCoverImageOptimization={cancelCoverImageOptimization}
          />
          <ManageEventActions
            activeStep={activeStep}
            isLastStep={isLastStep}
            isEditMode={isEditMode}
            submittingAction={submittingAction}
            submitDisabled={Boolean(coverImageSubmitBlockMessage)}
            requiresSaveAs={features.requiresSaveAs}
            partnerDraftEdit={isEditMode && features.requiresSaveAs && workflowStatus === 'draft'}
            partnerNonDraftEdit={
              isEditMode && features.requiresSaveAs && workflowStatus && workflowStatus !== 'draft'
            }
            createSubmitLabel={features.createSubmitLabel}
            draftSubmitLabel={features.draftSubmitLabel}
            editDraftSubmitLabel={features.editDraftSubmitLabel}
            editSaveChangesLabel={features.editSaveChangesLabel}
            onCancel={handleCancel}
            onBack={handleBack}
            onNext={handleNext}
            onSubmit={handleSubmit}
          />
        </FormPanel>
        <EventLivePreview
          form={form}
          eventData={eventData}
          partnerProfile={partnerProfile}
          isPartnerEvent={isPartner || features.requiresSaveAs}
        />
      </Box>
      <EventSubmissionSuccessModal
        open={Boolean(submittedEventSummary)}
        eventSummary={submittedEventSummary}
        onGoToEvents={() => {
          setSubmittedEventSummary(null);
          navigate(routes.list);
        }}
      />
    </Box>
  );
};

export default ManageEvent;
